// Fila de envio de fotos guardada no aparelho (IndexedDB). Roda igual na página e no
// service worker: assim a foto continua subindo mesmo se a pessoa sair ou fechar o app.
// A cópia local é apagada assim que o servidor confirma o recebimento.
// Este arquivo não pode importar nada da interface (também é usado dentro do service worker).

export interface UploadMeta { setor: string; legenda: string; isNc: boolean; ncId: string | null; gravidade?: 'leve' | 'moderada' | 'grave' }

export type QueueStatus = 'preparing' | 'waiting' | 'uploading' | 'confirming' | 'failed' | 'auth';

export interface QueueItem extends UploadMeta {
  id: string; // também é o clientId enviado ao servidor (reenvio não duplica)
  tid: string;
  visitId: string;
  blob: Blob;
  prepared: boolean;
  contentType: string;
  width: number | null;
  height: number | null;
  capturedAt: number;
  createdAt: number;
  status: QueueStatus;
  error?: string;
  attempts: number;
  nextAt: number;
  leaseUntil: number;
  photoId?: string;
}

export type QueueEvent =
  | { type: 'changed' }
  | { type: 'presigned'; visitId: string; createdNc: boolean }
  | { type: 'done'; visitId: string; photoId: string };

const DB_NAME = 'mesa-uploads';
const STORE = 'items';
const CHANNEL = 'mesa-uploads';
const LEASE_MS = 3 * 60_000;
const PARALLEL = 2;

// ── IndexedDB (com reserva em memória se o navegador bloquear o banco) ──

let dbPromise: Promise<IDBDatabase | null> | null = null;
const memory = new Map<string, QueueItem>();

function openDb(): Promise<IDBDatabase | null> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' }); };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }
  return dbPromise;
}

/** true quando as fotos ficam guardadas de verdade no aparelho (sobrevivem a fechar o app). */
export async function persistent() {
  return !!(await openDb());
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        if (!db) return resolve(undefined);
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        t.oncomplete = () => resolve(req ? req.result : undefined);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

export async function allItems(): Promise<QueueItem[]> {
  const db = await openDb();
  if (!db) return [...memory.values()];
  return ((await tx<QueueItem[]>('readonly', (s) => s.getAll())) ?? []).sort((a, b) => a.createdAt - b.createdAt);
}

export async function putItem(it: QueueItem) {
  if (!(await openDb())) { memory.set(it.id, it); return; }
  await tx('readwrite', (s) => { s.put(it); });
}

export async function deleteItem(id: string) {
  if (!(await openDb())) { memory.delete(id); return; }
  await tx('readwrite', (s) => { s.delete(id); });
}

/** Altera um item dentro de uma transação (atômico entre página e service worker). */
async function update(id: string, fn: (it: QueueItem) => QueueItem | null): Promise<QueueItem | null> {
  const db = await openDb();
  if (!db) {
    const cur = memory.get(id);
    const next = cur ? fn(cur) : null;
    if (next) memory.set(id, next);
    return next;
  }
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, 'readwrite');
    const s = t.objectStore(STORE);
    let out: QueueItem | null = null;
    const g = s.get(id);
    g.onsuccess = () => {
      const cur = g.result as QueueItem | undefined;
      out = cur ? fn(cur) : null;
      if (out) s.put(out);
    };
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
  });
}

export async function patchItem(id: string, p: Partial<QueueItem>) {
  const r = await update(id, (it) => ({ ...it, ...p }));
  notify({ type: 'changed' });
  return r;
}

// ── Avisos entre página e service worker ──

let channel: BroadcastChannel | null = null;
try { channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null; } catch { channel = null; }
const localListeners = new Set<(e: QueueEvent) => void>();

export function notify(e: QueueEvent) {
  localListeners.forEach((l) => l(e));
  try { channel?.postMessage(e); } catch { /* canal fechado */ }
}

export function onQueueEvent(l: (e: QueueEvent) => void) {
  localListeners.add(l);
  const h = (m: MessageEvent) => l(m.data as QueueEvent);
  channel?.addEventListener('message', h);
  return () => { localListeners.delete(l); channel?.removeEventListener('message', h); };
}

// ── Envio ──

class HttpError extends Error {
  constructor(public status: number, message: string, public code?: string) { super(message); }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method, credentials: 'same-origin',
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new HttpError(0, 'Sem conexão');
  }
  const text = await res.text();
  let data: { error?: string; code?: string } | null = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* não-JSON */ }
  if (!res.ok) throw new HttpError(res.status, data?.error ?? `Erro ${res.status}`, data?.code);
  return data as T;
}

/** Espera crescente entre tentativas: 3s, 6s, 12s… até 2 minutos. */
const backoff = (n: number) => Math.min(120_000, 3000 * 2 ** Math.max(0, n - 1));

/** Foto que não terminou de ser preparada (app fechado logo após a foto): depois de 15s vai a original. */
const RAW_AFTER_MS = 15_000;
const sendable = (it: QueueItem, now: number) =>
  it.status !== 'failed' && (it.prepared || now - it.createdAt > RAW_AFTER_MS);

async function claim(id: string, now: number): Promise<QueueItem | null> {
  return update(id, (it) => {
    if (!sendable(it, now) || it.leaseUntil > now || it.nextAt > now) return null;
    return { ...it, status: 'uploading', leaseUntil: now + LEASE_MS, error: undefined };
  });
}

async function sendOne(id: string) {
  const it = await claim(id, Date.now());
  if (!it) return;
  notify({ type: 'changed' });
  const base = `/api/t/${it.tid}/visits/${it.visitId}/photos`;
  try {
    const r = await call<{ photoId: string; uploaded?: boolean; uploadUrl?: string; headers?: Record<string, string>; createdNc: boolean }>('POST', `${base}/presign`, {
      contentType: it.contentType === 'image/heif' ? 'image/heic' : it.contentType || 'image/jpeg', bytes: it.blob.size, setor: it.setor, legenda: it.legenda || null, isNc: it.isNc, ncId: it.ncId,
      gravidade: it.gravidade ?? 'moderada', capturedAt: it.capturedAt, width: it.width, height: it.height, clientId: it.id,
    });
    await update(id, (x) => ({ ...x, photoId: r.photoId }));
    notify({ type: 'presigned', visitId: it.visitId, createdNc: r.createdNc });
    if (!r.uploaded) {
      let put: Response;
      try {
        put = await fetch(r.uploadUrl!, { method: 'PUT', headers: r.headers, body: it.blob, credentials: 'same-origin' });
      } catch {
        throw new HttpError(0, 'Sem conexão');
      }
      if (!put.ok) throw new HttpError(put.status >= 500 ? put.status : 0, `Envio recusado (${put.status})`);
      await patchItem(id, { status: 'confirming' });
      await call('POST', `${base}/${r.photoId}/confirm`);
    }
    await deleteItem(id);
    notify({ type: 'done', visitId: it.visitId, photoId: r.photoId });
    notify({ type: 'changed' });
  } catch (e) {
    const err = e instanceof HttpError ? e : new HttpError(0, 'Falha no envio');
    if (err.status === 404) {
      // Visita excluída (ou sem acesso): a foto não tem mais para onde ir.
      await deleteItem(id);
      notify({ type: 'changed' });
      return;
    }
    const attempts = it.attempts + 1;
    const permanent = err.status === 403 || err.status === 413 || (err.status === 422 && err.code !== undefined && err.code !== 'upload_mismatch');
    await update(id, (x) => ({
      ...x,
      attempts,
      leaseUntil: 0,
      status: err.status === 401 ? 'auth' : permanent ? 'failed' : 'waiting',
      error: err.status === 401 ? 'Entre de novo na sua conta para continuar enviando.' : err.status === 0 ? 'Sem conexão. Tentando de novo…' : err.message,
      nextAt: Date.now() + (err.status === 401 ? 30_000 : backoff(attempts)),
    }));
    notify({ type: 'changed' });
  }
}

let running: Promise<void> | null = null;
let wake: (() => void) | null = null;

/**
 * Envia tudo o que estiver na fila. Continua tentando enquanto houver fotos (com espera
 * crescente) por até `maxMs`. Uma chamada por vez em cada contexto; entre página e
 * service worker, a reserva (lease) no IndexedDB evita enviar a mesma foto duas vezes.
 */
export function processQueue(maxMs = 4 * 60_000): Promise<void> {
  // Já rodando: acorda a espera para olhar a fila de novo agora.
  if (running) { wake?.(); return running; }
  const until = Date.now() + maxMs;
  running = (async () => {
    try {
      while (Date.now() < until) {
        const now = Date.now();
        const items = await allItems();
        const live = items.filter((i) => i.status !== 'failed');
        if (!live.length) return;
        const due = live.filter((i) => sendable(i, now) && i.nextAt <= now && i.leaseUntil <= now).slice(0, PARALLEL);
        if (due.length) {
          await Promise.all(due.map((i) => sendOne(i.id)));
          continue;
        }
        // Nada para agora: espera a próxima tentativa (ou outra aba/worker terminar).
        const next = Math.min(...live.map((i) => Math.max(i.nextAt, i.leaseUntil, i.prepared ? 0 : i.createdAt + RAW_AFTER_MS + 100)));
        const wait = Math.max(500, Math.min(next - now, until - now, 15_000));
        await new Promise<void>((r) => { wake = r; setTimeout(r, wait); });
        wake = null;
      }
    } finally {
      running = null;
    }
  })();
  return running;
}

/** Libera para envio imediato (botão "tentar de novo" ou voltou a conexão). */
export async function retryAll(onlyIds?: string[]) {
  const items = await allItems();
  for (const it of items) {
    if (onlyIds && !onlyIds.includes(it.id)) continue;
    if (it.status === 'uploading' || it.status === 'confirming') continue;
    await update(it.id, (x) => ({ ...x, status: x.prepared ? 'waiting' : x.status, nextAt: 0, error: undefined }));
  }
  notify({ type: 'changed' });
}
